import { useState } from 'react';
import { MapView } from './components/MapView';
import { VehiclePanel } from './components/VehiclePanel';
import { LeaderboardPanel } from './components/LeaderboardPanel';
import { PlayerPanel } from './components/PlayerPanel';
import { GamePanel } from './components/GamePanel';
import { AuthModal } from './components/AuthModal';
import { useAuth } from './hooks/useAuth';
import { useSimulation } from './hooks/useSimulation';

function App() {
  const [activeTab, setActiveTab] = useState<'map' | 'vehicles' | 'games' | 'leaderboard' | 'profile'>('map');
  const [showAuth, setShowAuth] = useState(false);
  
  const auth = useAuth();
  const { user, isAuthenticated, logout } = auth || { user: null, isAuthenticated: false, logout: () => {} };
  const { vehicles, npcCounts, isConnected } = useSimulation();

  return (
    <div className="min-h-screen bg-lunar-900 text-lunar-100">
      {/* Header */}
      <header className="border-b border-lunar-700 bg-lunar-800">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🌙</span>
            <h1 className="text-xl font-bold">LuCo-1</h1>
            <span className="text-xs bg-lunar-700 px-2 py-1 rounded text-lunar-300">
              {isConnected ? '🟢 Live' : '🔴 Offline'}
            </span>
          </div>
          
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <>
                <span className="text-sm text-lunar-300">
                  👤 {user?.display_name}
                </span>
                <button onClick={logout} className="btn-secondary text-sm">
                  Logout
                </button>
              </>
            ) : (
              <button onClick={() => setShowAuth(true)} className="btn-primary text-sm">
                Login
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="border-b border-lunar-700 bg-lunar-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            {[
              { id: 'map', label: '🗺️ Map', icon: '🗺️' },
              { id: 'vehicles', label: '🚗 Vehicles', icon: '🚗' },
              { id: 'games', label: '🎮 Games', icon: '🎮' },
              { id: 'leaderboard', label: '🏆 Leaderboard', icon: '🏆' },
              { id: 'profile', label: '👤 Profile', icon: '👤' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-mars-400 border-b-2 border-mars-400'
                    : 'text-lunar-300 hover:text-lunar-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {activeTab === 'map' && (
          <MapView 
            vehicles={vehicles as unknown as Array<Record<string, unknown>>} 
            npcCounts={npcCounts as unknown as Record<string, unknown>}
          />
        )}
        
        {activeTab === 'vehicles' && (
          <VehiclePanel vehicles={vehicles as unknown as Array<Record<string, unknown>>} />
        )}
        
        {activeTab === 'games' && (
          <GamePanel />
        )}
        
        {activeTab === 'leaderboard' && (
          <LeaderboardPanel />
        )}
        
        {activeTab === 'profile' && (
          <PlayerPanel />
        )}
      </main>

      {/* Auth Modal */}
      {showAuth && (
        <AuthModal onClose={() => setShowAuth(false)} />
      )}
    </div>
  );
}

export default App;
